import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography } from '@mui/material';
import {
  ComposedChart,
  Line,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { fShortenNumber } from '../../../utils/formatNumber'; // <-- Make sure import path is correct

export default function TransactionAmountGraph({ trendData, height = '85%' }) {
  // Clean + prepare data
  const cleaned = useMemo(() => {
    if (!Array.isArray(trendData)) return [];

    return trendData
      .map((d) => {
        const ts = d.date ? new Date(d.date).getTime() : NaN;

        return {
          timestamp: ts,
          amount: Number(d.totalAmount ?? 0)
        };
      })
      .filter((d) => Number.isFinite(d.timestamp))
      .sort((a, b) => a.timestamp - b.timestamp);
  }, [trendData]);

  // Format X-axis dates
  const formatDate = (ts) => {
    const dt = new Date(ts);
    return dt.toLocaleDateString('en-IN', {
      month: 'short',
      day: 'numeric'
    });
  };

  if (!cleaned.length) {
    return (
      <div
        style={{
          height,
          textAlign: 'center',
          paddingTop: 20,
          color: 'gray',
          fontWeight: 600,
          fontSize: 12
        }}
      >
        No trend data available
      </div>
    );
  }

  // Custom Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload || !payload.length) return null;

    const dt = new Date(label);
    const dateLabel = dt.toLocaleDateString('en-IN', {
      day: 'numeric',
      month: 'short'
    });

    const rawAmount = payload[0]?.payload?.amount ?? 0;

    return (
      <div
        style={{
          background: '#fff',
          borderRadius: 12,
          padding: '10px',
          boxShadow: '0px 2px 8px rgba(0,0,0,0.15)',
          minWidth: 160
        }}
      >
        <div
          style={{
            fontWeight: 700,
            fontSize: 14,
            color: '#171D40',
            marginBottom: 12
          }}
        >
          {dateLabel}
        </div>

        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div
              style={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                background: '#4C8EF7'
              }}
            />
            <span
              style={{
                color: '#171D40',
                fontSize: 13,
                fontWeight: 700
              }}
            >
              Amount
            </span>
          </div>

          <span
            style={{
              color: '#171D40',
              fontSize: 13,
              fontWeight: 800
            }}
          >
            {fShortenNumber(rawAmount)}
          </span>
        </div>
      </div>
    );
  };

  return (
    <Box sx={{ width: '100%', height }}>
      <ResponsiveContainer>
        <ComposedChart data={cleaned} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
          {/* GRADIENT */}
          <defs>
            <linearGradient id="amountGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#457AEF" />
              <stop offset="100%" stopColor="#FFFFFF" />
            </linearGradient>
          </defs>

          <CartesianGrid stroke="#eee" vertical={false} />

          {/* X-AXIS */}
          <XAxis
            dataKey="timestamp"
            tickFormatter={formatDate}
            tick={{ fill: '#98A1B3', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            interval={0}
            minTickGap={0}
          />

          {/* Y-AXIS WITH SHORTENED NUMBERS */}
          <YAxis
            tick={{ fill: '#98A1B3', fontSize: 12 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={(v) => fShortenNumber(v)}
          />

          <Tooltip content={<CustomTooltip />} cursor={{ stroke: '#ccc', strokeWidth: 1 }} />

          {/* AREA */}
          <Area
            type="monotone"
            dataKey="amount"
            stroke="#4C8EF7"
            fill="url(#amountGrad)"
            strokeWidth={2}
            dot={false}
            legendType="none"
            tooltipType="none"
          />

          {/* LINE */}
          <Line
            type="monotone"
            dataKey="amount"
            name="Total Amount"
            stroke="#4C8EF7"
            strokeWidth={2}
            dot={false}
            activeDot={{ r: 6 }}
          />
        </ComposedChart>
      </ResponsiveContainer>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mt: 1,
          flexWrap: 'wrap',
          justifyContent: 'center'
        }}
      >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 0.8,
              py: 0.5,
              borderRadius: '5px',
              backgroundColor: '#FFFFFF',
              boxShadow: '0px 4px 12px rgba(0,0,0,0.08)'
            }}
          >
            <svg width="5" height="5" viewBox="0 0 8 8">
              <circle cx="4" cy="4" r="4" fill="#457AEF" />
            </svg>
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 700,
                color: '#171D40',
                whiteSpace: 'nowrap'
              }}
            >
              Transaction Amount
            </Typography>
          </Box>
      </Box>
    </Box>
  );
}

TransactionAmountGraph.propTypes = {
  trendData: PropTypes.array.isRequired,
  height: PropTypes.number
};
